import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
    CheckCircle,
    Clock,
    Loader2,
    Package,
    Receipt,
    ArrowRight,
    AlertCircle,
    RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { translate as defaultTranslate } from "@/i18n";
import type { Transaction } from "../client-ecommerce";
import {
    clearPendingStripeCheckout,
    readPendingStripeCheckout,
} from "./checkout-pending";

const TERMINAL_STATUSES = new Set(["completed", "paid", "failed", "cancelled", "refunded"]);
const POLL_MS = 1500;
const MAX_POLLS = 40;

export interface OrderSuccessPageProps {
    onFetchTransaction: (id: string) => Promise<Transaction>;
    onFetchTransactions: () => Promise<Transaction[]>;
    onFinalizeStripeOrder: (
        transactionId: string,
        data: { shipping: Record<string, unknown>; paymentIntent?: { id: string; status: string } },
    ) => Promise<void>;
    onUpdateTransaction: (id: string, patch: Record<string, unknown>) => Promise<Transaction>;
    /** Legacy fallback if no pending transaction exists on redirect return. */
    onPlaceOrder: (data: {
        shipping: Record<string, unknown>;
        paymentMethod: string;
        external_order_id?: string;
        product_info?: unknown[];
        total_amount?: number;
        currency?: string;
        metadata?: Record<string, unknown>;
    }) => Promise<Transaction>;
    onNavigate: (path: string) => void;
    purchasesPath?: string;
    contactEmail?: string;
    t?: (key: string) => string;
}

function isConfirmed(status: string): boolean {
    return status === "completed" || status === "paid";
}

export function OrderSuccessPage({
    onFetchTransaction,
    onFetchTransactions,
    onFinalizeStripeOrder,
    onUpdateTransaction,
    onPlaceOrder,
    onNavigate,
    purchasesPath = "/profile/purchases",
    contactEmail = "sales@polymech.info",
    t: tProp,
}: OrderSuccessPageProps) {
    const t = tProp ?? defaultTranslate;
    const [searchParams] = useSearchParams();
    const orderIdParam = searchParams.get("order");
    const paymentIntentId = searchParams.get("payment_intent");
    const redirectStatus = searchParams.get("redirect_status");

    const [transaction, setTransaction] = useState<Transaction | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [polling, setPolling] = useState(false);

    const pollUntilSettled = useCallback(async (id: string) => {
        setPolling(true);
        let latest: Transaction | null = null;
        for (let i = 0; i < MAX_POLLS; i++) {
            latest = await onFetchTransaction(id);
            setTransaction(latest);
            if (TERMINAL_STATUSES.has(latest.status)) break;
            await new Promise((r) => setTimeout(r, POLL_MS));
        }
        setPolling(false);
        return latest;
    }, [onFetchTransaction]);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setLoading(true);
            setError(null);

            try {
                if (orderIdParam) {
                    const tx = await pollUntilSettled(orderIdParam);
                    if (mounted && tx) clearPendingStripeCheckout();
                    return;
                }

                if (paymentIntentId && redirectStatus === "succeeded") {
                    const pending = readPendingStripeCheckout();
                    const all = await onFetchTransactions();
                    let existing = all.find((row) => row.external_order_id === paymentIntentId);
                    if (!existing && pending?.transactionId) {
                        existing = all.find((row) => row.id === pending.transactionId);
                    }

                    if (existing) {
                        if (pending?.shipping) {
                            await onFinalizeStripeOrder(existing.id, {
                                shipping: pending.shipping,
                                paymentIntent: { id: paymentIntentId, status: "succeeded" },
                            });
                        } else {
                            await onUpdateTransaction(existing.id, {
                                metadata: {
                                    stripe_payment_intent_id: paymentIntentId,
                                    stripe_status: "succeeded",
                                },
                            });
                        }
                        if (mounted) {
                            clearPendingStripeCheckout();
                            await pollUntilSettled(existing.id);
                        }
                        return;
                    }

                    if (pending?.transactionId) {
                        await onFinalizeStripeOrder(pending.transactionId, {
                            shipping: pending.shipping,
                            paymentIntent: { id: paymentIntentId, status: "succeeded" },
                        });
                        if (mounted) {
                            clearPendingStripeCheckout();
                            await pollUntilSettled(pending.transactionId);
                        }
                        return;
                    }

                    if (pending) {
                        const created = await onPlaceOrder({
                            shipping: pending.shipping,
                            paymentMethod: "stripe",
                            external_order_id: paymentIntentId,
                            product_info: pending.items,
                            total_amount: pending.subtotal,
                            currency: pending.currency,
                            metadata: {
                                stripe_payment_intent_id: paymentIntentId,
                                stripe_status: "succeeded",
                            },
                        });
                        if (mounted) {
                            clearPendingStripeCheckout();
                            await pollUntilSettled(created.id);
                        }
                        return;
                    }

                    if (mounted) {
                        setError(t("Payment succeeded but checkout session expired. Your order may still be processing — check My Purchases or contact support."));
                    }
                    return;
                }

                if (paymentIntentId && redirectStatus === "failed") {
                    if (mounted) setError(t("Payment was not completed. Please try again."));
                    return;
                }

                if (mounted) setError(t("No order information found."));
            } catch (err) {
                console.error("Order success page failed:", err);
                if (mounted) {
                    setError(t("Could not load your order. It may still appear under My Purchases shortly."));
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };

        void load();
        return () => { mounted = false; };
    }, [
        orderIdParam,
        paymentIntentId,
        redirectStatus,
        onFetchTransactions,
        onFinalizeStripeOrder,
        onUpdateTransaction,
        onPlaceOrder,
        pollUntilSettled,
        t,
    ]);

    const products = Array.isArray(transaction?.product_info) ? transaction.product_info : [];
    const confirmed = transaction ? isConfirmed(transaction.status) : false;
    const failed = transaction?.status === "failed";

    if (loading && !transaction) {
        return (
            <div className="flex flex-col items-center justify-center gap-4 py-24">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">{t("Confirming your order…")}</p>
            </div>
        );
    }

    if (error && !transaction) {
        return (
            <div className="mx-auto max-w-lg px-4 py-16 space-y-6 text-center">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
                <h1 className="text-2xl font-bold">{t("Order confirmation unavailable")}</h1>
                <p className="text-muted-foreground">{error}</p>
                <p className="text-sm text-muted-foreground">
                    {t("Questions? Contact")}{" "}
                    <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
                        {contactEmail}
                    </a>
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={() => onNavigate(purchasesPath)}>{t("View My Purchases")}</Button>
                    <Button variant="outline" onClick={() => onNavigate("/")}>{t("Continue Shopping")}</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-2xl px-4 py-10 space-y-6">
            <div className="text-center space-y-3">
                <div
                    className={cn(
                        "mx-auto flex h-16 w-16 items-center justify-center rounded-full",
                        failed ? "bg-destructive/10" : "bg-primary/10",
                    )}
                >
                    {failed ? (
                        <AlertCircle className="h-8 w-8 text-destructive" />
                    ) : confirmed ? (
                        <CheckCircle className="h-8 w-8 text-primary" />
                    ) : (
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    )}
                </div>
                <h1 className="text-2xl font-bold">
                    {failed
                        ? t("Payment failed")
                        : confirmed
                            ? t("Thank you for your order!")
                            : t("Payment received")}
                </h1>
                <p className="text-muted-foreground">
                    {failed
                        ? t("Something went wrong while processing your payment.")
                        : confirmed
                            ? t("Your order is confirmed. A confirmation email will be sent if applicable.")
                            : t("We're confirming your payment with the bank. This usually takes a few seconds.")}
                </p>
            </div>

            {transaction && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <CardTitle className="text-base">
                                {t("Order")} #{transaction.id.slice(0, 8).toUpperCase()}
                            </CardTitle>
                            <Badge
                                variant={confirmed ? "default" : failed ? "destructive" : "secondary"}
                                className="flex items-center gap-1"
                            >
                                {polling || (!confirmed && !failed) ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : confirmed ? (
                                    <CheckCircle className="h-3 w-3" />
                                ) : (
                                    <Clock className="h-3 w-3" />
                                )}
                                {confirmed ? t("Confirmed") : failed ? t("Failed") : t("Processing")}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{t("Total")}</span>
                            <span className="font-semibold tabular-nums">
                                {transaction.currency} {Number(transaction.total_amount).toFixed(2)}
                            </span>
                        </div>

                        {products.length > 0 && (
                            <div className="space-y-2 border-t pt-4">
                                {products.map((item: any, idx: number) => (
                                    <div key={idx} className="flex items-center gap-3 text-sm">
                                        {item.image && (
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="h-10 w-10 rounded object-cover shrink-0"
                                            />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium truncate">{item.title || t("Product")}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {t("Qty")} {item.quantity || 1}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!confirmed && !failed && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground border-t pt-4">
                                <RefreshCw className={cn("h-4 w-4", polling && "animate-spin")} />
                                {t("Waiting for payment confirmation…")}
                            </div>
                        )}

                        {transaction.metadata?.stripe_receipt_url && (
                            <div className="border-t pt-4">
                                <a
                                    href={transaction.metadata.stripe_receipt_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary hover:underline inline-flex items-center gap-1.5"
                                >
                                    <Receipt className="h-4 w-4" />
                                    {t("View receipt")}
                                </a>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {!failed && (
                <p className="text-center text-sm text-muted-foreground">
                    {t("Questions? Contact")}{" "}
                    <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
                        {contactEmail}
                    </a>
                </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
                <Button
                    className="flex-1"
                    onClick={() =>
                        onNavigate(
                            transaction
                                ? `${purchasesPath}?order=${transaction.id}`
                                : purchasesPath,
                        )
                    }
                >
                    <Package className="h-4 w-4 mr-2" />
                    {t("View My Purchases")}
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => onNavigate("/")}>
                    {t("Continue Shopping")}
                    <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
            </div>
        </div>
    );
}
